import { type FocusEvent, useState } from 'react';
import { AgAutocomplete, type AgAutocompleteProps } from 'src/components/ui/AgAutocomplete';
import { type CommodityOption, useCommodityOptions } from './commodityOptions';

/** The fields a picker shows; a record's selected commodity (read with the record) carries the same ones. */
export type CommodityPickerValue = Pick<CommodityOption, 'id' | 'name'>;

export type CommodityPickerProps = Omit<
  AgAutocompleteProps<CommodityPickerValue>,
  'options' | 'getOptionLabel' | 'filterOptions' | 'onInputChange' | 'isLoading'
>;

/**
 * A commodity picker searched on the server (TAS-3660 NN07). Nothing is read until the field gets
 * focus; then the first page loads and typing searches. The selected commodity comes from the record
 * itself, so its name shows without a read.
 */
export function CommodityPicker({
  onChange,
  onFocus,
  getOptionKey = (option: CommodityPickerValue) => option.id,
  isOptionEqualToValue = (option: CommodityPickerValue, value: CommodityPickerValue) => option.id === value.id,
  ...props
}: CommodityPickerProps) {
  const [opened, setOpened] = useState(false);
  const [searchText, setSearchText] = useState('');
  const { commodities, loading } = useCommodityOptions({ searchText, skip: !opened });

  return (
    <AgAutocomplete<CommodityPickerValue>
      {...props}
      options={commodities}
      getOptionLabel={(option) => option.name}
      getOptionKey={getOptionKey}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={false}
      isLoading={opened && loading}
      onFocus={(event: FocusEvent<HTMLInputElement>) => {
        setOpened(true);
        onFocus?.(event);
      }}
      onInputChange={setSearchText}
      onChange={(value) => {
        // A pick ends the search: the next open shows the (cached) first page again.
        setSearchText('');
        onChange?.(value);
      }}
    />
  );
}
